import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Modal, Input, Table, Button, Row, Col, Tag, Tooltip } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import WorkOrderStore from 'stores/WorkOrderStore';
import { ShopStore, UserStore } from 'stores';
import { observer } from 'mobx-react';
import TableStore from 'stores/abstract/TableStore';
import debounce from 'lodash/debounce';
import ExternalIcon from 'assets/icons/external.svg?react';
import moment from 'moment';
import { WorkOrderRepairPriorityEnum } from 'stores/Classes/WorkOrderObject';
import _ from 'lodash';
import { EventBus } from 'stores/EventBus';
import * as Sentry from '@sentry/react';

const WorkOrderSelectionModal = ({
  isVisible,
  onCancel,
  onSelect,
  title = 'Select Work Order',
}) => {
  // Preserve state in localStorage to survive slideout interactions
  const getPreservedState = () => {
    try {
      const preserved = localStorage.getItem('workOrderSelectionModalState');
      return preserved ? JSON.parse(preserved) : null;
    } catch (error) {
      console.error('Error parsing preserved state:', error);
      Sentry.captureException(error, {
        tags: {
          component: 'WorkOrderSelectionModal',
          method: 'getPreservedState',
        },
        extra: {
          error,
        },
      });
      return null;
    }
  };

  const setPreservedState = (newState) => {
    try {
      localStorage.setItem(
        'workOrderSelectionModalState',
        JSON.stringify(newState)
      );
    } catch (error) {
      console.error('Error saving preserved state:', error);
      Sentry.captureException(error, {
        tags: {
          component: 'WorkOrderSelectionModal',
          method: 'setPreservedState',
        },
        extra: {
          error,
        },
      });
    }
  };

  const clearPreservedState = () => {
    localStorage.removeItem('workOrderSelectionModalState');
  };

  const [state, setState] = useState(() => {
    // Try to restore preserved state, otherwise use defaults
    const preserved = getPreservedState();
    if (preserved && isVisible) {
      return preserved;
    }
    return {
      searchValue: '',
      selectedWorkOrder: null,
      loading: false,
      data: [],
      pagination: {
        current: 1,
        pageSize: 10,
        total: 0,
      },
      shopUsers: [],
      shopWorkOrderStatuses: [],
    };
  });

  // Custom state setter that preserves state in localStorage
  const setStateWithPreservation = (updater) => {
    setState((prevState) => {
      const newState =
        typeof updater === 'function' ? updater(prevState) : updater;
      setPreservedState(newState);
      return newState;
    });
  };

  const tableStoreRef = useRef(new TableStore());
  const usersTableStoreRef = useRef(new TableStore());

  const debouncedSearch = useCallback(
    debounce((value) => {
      fetchWorkOrders(1, state.pagination.pageSize, value);
    }, 300),
    []
  );

  const loadShopUsers = async () => {
    try {
      const shopUsers = await UserStore.fetchUsersForTable(
        {
          offset: 0,
          limit: 250,
          filter: null,
        },
        usersTableStoreRef.current
      );
      setStateWithPreservation((prev) => ({
        ...prev,
        shopUsers: shopUsers || [],
      }));
    } catch (error) {
      console.error('Error loading shop users:', error);
      Sentry.captureException(error, {
        tags: {
          component: 'WorkOrderSelectionModal',
          method: 'loadShopUsers',
        },
        extra: {
          error,
        },
      });
    }
  };

  const loadShopWorkOrderStatuses = async () => {
    try {
      const shopId = ShopStore.currentShop.id;
      const response = await WorkOrderStore.getShopWorkOrderStatuses(shopId);
      const statuses = _.get(response, 'data', []);
      setStateWithPreservation((prev) => ({
        ...prev,
        shopWorkOrderStatuses: statuses.map((status) => ({
          id: status.id,
          key: status.key,
          name: status.description,
          description: status.description,
          color: status.color,
          text: status.description,
          value: status.key,
          is_default: status.is_default,
        })),
      }));
    } catch (error) {
      console.error('Error loading work order statuses:', error);
      Sentry.captureException(error, {
        tags: {
          component: 'WorkOrderSelectionModal',
          method: 'loadShopWorkOrderStatuses',
        },
        extra: {
          error,
        },
      });
    }
  };

  const fetchWorkOrders = async (page = 1, pageSize = 10, search = '') => {
    setStateWithPreservation((prev) => ({ ...prev, loading: true }));

    try {
      const shopId = ShopStore.currentShop.id;
      const filters = {
        offset: (page - 1) * pageSize,
        limit: pageSize,
        sort: '-created_at',
      };

      if (search) {
        filters.filter = { search };
      }

      const response = await WorkOrderStore.fetchWorkOrdersForTable(
        filters,
        tableStoreRef.current,
        shopId,
        false
      );

      const { data, meta } = response || { data: [], meta: { count: 0 } };
      setStateWithPreservation((prev) => ({
        ...prev,
        data,
        pagination: {
          current: page,
          pageSize,
          total: meta?.total || meta?.count || 0,
        },
        loading: false,
      }));
    } catch (error) {
      console.error('Error fetching work orders:', error);
      Sentry.captureException(error, {
        tags: {
          component: 'WorkOrderSelectionModal',
          method: 'fetchWorkOrders',
        },
        extra: {
          error,
        },
      });
      setStateWithPreservation((prev) => ({ ...prev, loading: false }));
    }
  };

  useEffect(() => {
    if (isVisible) {
      loadShopUsers();
      loadShopWorkOrderStatuses();
      fetchWorkOrders();
      setStateWithPreservation((prev) => ({
        ...prev,
        selectedWorkOrder: null,
      }));
    }
  }, [isVisible]);

  const handleSearch = (value) => {
    setStateWithPreservation((prev) => ({ ...prev, searchValue: value }));
    debouncedSearch(value);
  };

  const handleTableChange = (pagination) => {
    fetchWorkOrders(pagination.current, pagination.pageSize, state.searchValue);
  };

  const handleRowClick = (record) => {
    setStateWithPreservation((prev) => ({
      ...prev,
      selectedWorkOrder: record,
    }));
  };

  const handleSelect = () => {
    if (state.selectedWorkOrder) {
      onSelect(state.selectedWorkOrder);
      // Clear preserved state when work order is selected
      clearPreservedState();
    }
  };

  const openWorkOrderSlideOut = async (workOrderId) => {
    try {
      // Fetch the work order data
      const workOrderData = await WorkOrderStore.getWorkOrderById(workOrderId);

      // Open the slide-out modal
      EventBus.$emit('dashboardWrapper/OPEN_SLIDE_OUT', {
        type: 'work-order',
        data: workOrderData,
        title: `Work Order #${workOrderData.invoice_number || workOrderData.id}`,
        selectedCarId: workOrderData.id_car,
      });
    } catch (error) {
      console.error('Error fetching work order:', error);
      Sentry.captureException(error, {
        tags: {
          component: 'WorkOrderSelectionModal',
          method: 'openWorkOrderSlideOut',
        },
        extra: {
          error,
          workOrderId,
        },
      });
    }
  };

  const renderStatusTag = (status) => {
    if (!status) return <Tag color="default">N/A</Tag>;

    const statusObj = Array.isArray(state.shopWorkOrderStatuses)
      ? state.shopWorkOrderStatuses.find((s) => s.key === status)
      : null;
    const color = statusObj?.color || '#1890ff';
    const name = statusObj?.description || statusObj?.name || status;

    return <Tag color={color}>{name}</Tag>;
  };

  const columns = [
    {
      title: 'Order #',
      dataIndex: 'invoice_number',
      key: 'invoice_number',
      width: 140,
      render: (wo, row) => {
        const orderNumber = `#${row.w_ord_id ? row.w_ord_id : wo}`;
        return (
          <span
            style={{
              color: '#1890ff',
              fontWeight: 'bold',
              cursor: 'pointer',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '4px',
            }}
            onClick={(e) => {
              e.stopPropagation();
              openWorkOrderSlideOut(row.id);
            }}
          >
            {orderNumber}
            <ExternalIcon style={{ width: '12px', height: '12px' }} />
          </span>
        );
      },
    },
    {
      title: 'Unit ID',
      dataIndex: 'car_name',
      key: 'car_name',
      width: 120,
      render: (carName) => carName || 'N/A',
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 120,
      render: (status) => renderStatusTag(status),
    },
    {
      title: 'Created At',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 140,
      render: (date, row) => {
        const user = state.shopUsers.find((u) => u.userId === row.created_by);
        return (
          <Row>
            <Col span={24}>
              <div>{date ? moment(date).format('ll') : 'N/A'}</div>
            </Col>
            {user && (
              <Col span={24}>
                <div style={{ fontSize: '12px', color: '#666' }}>
                  {`${user.firstName} ${user.lastName}`.trim() || user.email}
                </div>
              </Col>
            )}
          </Row>
        );
      },
    },
  ];

  const rowSelection = {
    type: 'radio',
    selectedRowKeys: state.selectedWorkOrder
      ? [state.selectedWorkOrder.id]
      : [],
    onChange: (selectedRowKeys, selectedRows) => {
      if (selectedRows.length > 0) {
        setStateWithPreservation((prev) => ({
          ...prev,
          selectedWorkOrder: selectedRows[0],
        }));
      }
    },
  };

  const handleCancel = () => {
    clearPreservedState();
    onCancel();
  };

  return (
    <Modal
      title={title}
      open={isVisible}
      onCancel={handleCancel}
      width={800}
      zIndex={100}
      footer={[
        <Button key="cancel" onClick={onCancel}>
          Cancel
        </Button>,
        <Button
          key="select"
          type="primary"
          onClick={handleSelect}
          disabled={!state.selectedWorkOrder}
        >
          Select Work Order
        </Button>,
      ]}
    >
      <Row style={{ marginBottom: 16 }}>
        <Col span={24}>
          <Input
            placeholder="Search by Order #, or Unit ID"
            value={state.searchValue}
            onChange={(e) => handleSearch(e.target.value)}
            prefix={<SearchOutlined />}
            allowClear
            onPressEnter={(e) => handleSearch(e.target.value)}
            suffix={
              <SearchOutlined
                onClick={() => handleSearch(state.searchValue)}
                style={{ cursor: 'pointer' }}
              />
            }
          />
        </Col>
      </Row>

      <Table
        columns={columns}
        dataSource={state.data}
        rowKey="id"
        loading={state.loading}
        pagination={state.pagination}
        onChange={handleTableChange}
        rowSelection={rowSelection}
        onRow={(record) => ({
          onClick: () => handleRowClick(record),
          style: {
            cursor: 'pointer',
            backgroundColor:
              state.selectedWorkOrder?.id === record.id
                ? '#f0f8ff'
                : 'transparent',
          },
        })}
        size="middle"
      />
    </Modal>
  );
};

export default observer(WorkOrderSelectionModal);
